import React, { useContext, useEffect, useCallback, useState } from 'react';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { equipment, equipments } from 'utilities/api';
import axios from 'axios';
import { useToast } from '../components/ToastContainer';
import { EquipmentItem } from '../components/EquipmentDropdown/types';
import { ApiResource } from '../utilities/api.types';

export interface EquipmentApiResponse {
  id: string;
  name: string;
  type?: string;
  currentStatus?: string;
  currentStatusDate?: string;
  year?: string;
  make?: string;
  model?: string;
  unitID?: string; // Note: API uses unitID but we map to unitId
  serialNumber?: string;
  licenseNumber?: string;
  purchasePrice?: number;
  purchasePriceDate?: string; // Note: API uses purchasePriceDate but we map to purchaseDate
  currentValue?: number;
  currentValueDate?: string;
  notes?: string;
  statusHistory?: { current: boolean; status: string; dateEpoch: number }[];
  valuations?: { type: string; amount: number; dateEpoch: number }[];
}

export interface UseEquipmentDataReturn {
  equipments: EquipmentItem[];
  loading: boolean;
  saving: boolean;
  loadEquipment: () => void;
  createEquipment: (
    values: Omit<EquipmentItem, 'id'>
  ) => Promise<EquipmentItem | undefined>;
  refetchEquipment: () => void;
}

interface EquipmentListResponse {
  data: {
    results: EquipmentApiResponse[];
  };
}

/**
 * Format equipment data from API response to internal format
 * @param item API response item
 * @returns Formatted equipment item
 */
const formatEquipmentData = (item: EquipmentApiResponse): EquipmentItem => {
  const currentStatus = item.statusHistory?.find(
    (status: { current: boolean }) => status.current
  );

  const purchaseValuation = item.valuations?.find(
    (valuation: { type: string }) => valuation.type === 'purchase'
  );

  const currentValuation = item.valuations?.find(
    (valuation: { type: string }) => valuation.type === 'current'
  );

  return {
    id: item.id,
    name: item.name,
    type: item.type,
    currentStatus: currentStatus?.status || item.currentStatus,
    currentStatusDate: currentStatus?.dateEpoch
      ? new Date(currentStatus.dateEpoch).toISOString()
      : item.currentStatusDate,
    year: item.year,
    make: item.make,
    model: item.model,
    unitId: item.unitID,
    serialNumber: item.serialNumber,
    licenseNumber: item.licenseNumber,
    purchasePrice: purchaseValuation?.amount || item.purchasePrice,
    purchaseDate: purchaseValuation?.dateEpoch
      ? new Date(purchaseValuation.dateEpoch).toISOString()
      : item.purchasePriceDate,
    currentValue: currentValuation?.amount || item.currentValue,
    currentValueDate: currentValuation?.dateEpoch
      ? new Date(currentValuation.dateEpoch).toISOString()
      : item.currentValueDate,
    notes: item.notes
  };
};

/**
 * Hook for managing equipment data
 * @returns Equipment data and methods
 */
const useEquipmentData = (): UseEquipmentDataReturn => {
  const [, dispatch] = (useContext(Context) as unknown) as [
    Record<string, unknown>,
    React.Dispatch<any>
  ];
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [skipCleanup, setSkipCleanup] = useState(false);
  const toast = useToast();

  // equipment to ApiResource type to avoid TypeScript errors
  const equipmentApi = equipment as ApiResource<EquipmentApiResponse>;
  const equipmentsApi = equipments as ApiResource<{
    results: EquipmentApiResponse[];
  }>;

  /**
   * Sort equipment items alphabetically by name
   * @param items - Equipment items to sort
   * @returns Sorted equipment items
   */
  const sortEquipments = useCallback(
    (items: EquipmentItem[]): EquipmentItem[] => {
      return [...items].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
    },
    []
  );

  /**
   * Load equipment data
   * @returns Cleanup function
   */
  const loadEquipment = useCallback(
    () => {
      setLoading(true);

      const fetchResult = equipmentsApi.fetch();
      fetchResult.promise
        .then((res: EquipmentListResponse) => {
          const equipmentList = res.data.results.map(formatEquipmentData);
          const sorted = sortEquipments(equipmentList);
          setEquipmentItems(sorted);
          return res.data.results;
        })
        .catch(catchCancel)
        .catch(err => {
          parseServerError(dispatch)(err);
        })
        .finally(() => {
          setLoading(false);
        });

      // cleanup function
      return () => {
        if (skipCleanup) return;
        fetchResult.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, equipmentsApi, sortEquipments, skipCleanup]
  );

  const refetchEquipment = useCallback(() => {
    setSkipCleanup(true);
    loadEquipment();
  }, [loadEquipment]);

  /**
   * Create new equipment
   * @param values - Equipment data
   * @returns Promise with created equipment data
   */
  const createEquipment = useCallback(
    async (
      values: Omit<EquipmentItem, 'id'>
    ): Promise<EquipmentItem | undefined> => {
      setSaving(true);

      try {
        const { data } = await equipmentApi.create(values).promise;
        const createdEquipment = formatEquipmentData(data);
        setEquipmentItems(prev => sortEquipments([...prev, createdEquipment]));
        return createdEquipment;
      } catch (error) {
        if (axios.isCancel(error)) {
          catchCancel(error as Error);
        } else {
          toast.error('Unable to save new equipment.', {
            content:
              'An error prevented your equipment from being saved. Please try again.',
            supportButton: true,
            timeout: 7000,
            customStyle: { width: '400px' }
          });
        }
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, equipmentApi, toast, loadEquipment]
  );

  // need to implement update in the future

  useEffect(() => {
    if (skipCleanup) {
      loadEquipment();
      setSkipCleanup(false);
      return;
    }

    const cancel = loadEquipment();
    // eslint-disable-next-line consistent-return
    return () => {
      cancel();
    };
  }, [loadEquipment, skipCleanup]);

  return {
    equipments: equipmentItems,
    loading,
    saving,
    loadEquipment,
    createEquipment,
    refetchEquipment
  };
};

export default useEquipmentData;
